import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import heroImage from "../assets/hero-image.png";
import heroImageMobile from "../assets/hero-image-mobile.png";
import eventImg from "../assets/event-coverage.webp";
import framingImg from "../assets/picture-framing.jpg";
import studioImg from "../assets/studio-session.jpg";
import albumImg from "../assets/albums.webp";
import BookSessionCTA from "../components/BookSessionCTA";
import ReviewsSection from "../components/ReviewsSection";
import HomeGallerySection from "../components/HomeGallerySection";
import HomeShopPreviewSection from "../components/HomeShopPreviewSection";
import { useApi } from "../utils/Api";
import SEO from "../components/SEO";

const services = [
  {
    title: "Event Coverage",
    desc: "Photo + video coverage for weddings, birthdays, corporate events, and special celebrations.",
    cta: "Explore coverage →",
    img: eventImg,
    to: "/packages",
  },
  {
    title: "Bespoke Picture Framing",
    desc: "Any size, any style. Custom frames designed to display your best photos beautifully.",
    img: framingImg,
    to: "/shop",
    cta: "Shop frames →",
  },
  {
    title: "Studio Photography Sessions",
    desc: "Modern studio photoshoots for portraits, family shoots, graduations, maternity, and personal branding.",
    cta: "Book a studio session →",
    img: studioImg,
    to: "/book",
  },
  {
    title: "Albums & Prints",
    desc: "Premium photo albums and professional-grade prints to preserve your moments for years to come.",
    cta: "Contact us →",
    img: albumImg,
    to: "/contact",
  },
];

const money = (n) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(Number(n || 0));

const ensureCoverageText = (value) => {
  if (!value) return "";

  const original = String(value).trim();
  const lower = original.toLowerCase();

  if (lower.includes("coverage")) {
    return original;
  }

  return `${original} coverage`;
};

export default function Home() {
  const { apiFetch } = useApi();

  const [packages, setPackages] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(true);
  const [packagesError, setPackagesError] = useState("");
  const [shopProducts, setShopProducts] = useState([]);
  const [loadingShopProducts, setLoadingShopProducts] = useState(true);
  const [shopProductsError, setShopProductsError] = useState("");

  const loadPackages = useCallback(async () => {
    try {
      setLoadingPackages(true);
      setPackagesError("");

      const res = await apiFetch("get_wedding_packages");

      if (!res?.success) {
        throw new Error(res?.message || "Failed to load wedding packages.");
      }

      const mappedPackages = (Array.isArray(res.data) ? res.data : []).map(
        (item) => ({
          id: Number(item.id),
          name: item.name,
          price: Number(item.price),
          coverage: ensureCoverageText(item.coverage),
          features: Array.isArray(item.features) ? item.features : [],
        }),
      );

      setPackages(mappedPackages);
    } catch (err) {
      setPackages([]);
      setPackagesError(err.message || "Unable to load wedding packages.");
    } finally {
      setLoadingPackages(false);
    }
  }, [apiFetch]);

  const loadShopProducts = useCallback(async () => {
    try {
      setLoadingShopProducts(true);
      setShopProductsError("");

      const res = await apiFetch("get_home_shop_products");

      if (!res?.success) {
        throw new Error(res?.message || "Failed to load items.");
      }

      const mappedProducts = (Array.isArray(res.data) ? res.data : []).map(
        (item) => ({
          id: Number(item.id),
          name: item.name || "",
          details: item.details || "",
          base_price: Number(item.base_price ?? 0),
          category_name: item.category_name || "",
          image_url: item.image_url || "",
          image_id: item.image_id ? Number(item.image_id) : null,
        }),
      );
      setShopProducts(mappedProducts);
    } catch (err) {
      setShopProducts([]);
      setShopProductsError(err.message || "Unable to load items.");
    } finally {
      setLoadingShopProducts(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    loadPackages();
    loadShopProducts();
  }, [loadPackages, loadShopProducts]);

  const displayedPackages = useMemo(() => packages.slice(0, 3), [packages]);

  return (
    <>
      <SEO
        title="Kinews Studio Limited – Photography Studio, Studio Rentals, Prints & Personalised Gifts"
        description="Professional photography studio offering event coverage, studio rentals, high-quality prints and personalised gifts."
        url={window.location.href}
      />
      <div>
        {/* HERO */}
        <section className="relative flex min-h-[100dvh] items-center overflow-hidden">
          <picture className="absolute inset-0">
            <source media="(max-width: 767px)" srcSet={heroImageMobile} />
            <img
              src={heroImage}
              alt=""
              className="h-full w-full object-cover object-right md:object-center"
            />
          </picture>

          <div className="absolute inset-0 bg-black/55" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-black" />

          <motion.div
            className="absolute -inset-40 bg-[radial-gradient(circle,rgba(236,72,153,0.12),transparent_55%)]"
            animate={{ x: [0, 30, 0], y: [0, -20, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          />

          <div className="relative mx-auto w-full max-w-7xl px-6 pb-16 pt-28">
            <div className="flex flex-col items-start">
              <div className="mt-6 max-w-4xl text-left">
                <motion.h1
                  className="text-3xl font-semibold leading-[1.25] tracking-tight sm:text-4xl md:text-5xl lg:text-6xl"
                  initial={{ opacity: 0, y: 22, filter: "blur(10px)" }}
                  animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                  transition={{ duration: 1, delay: 0.1 }}
                >
                  Every moment has a story.
                  <motion.span
                    className="block text-white/60"
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 0.35 }}
                  >
                    We help you remember it beautifully.
                  </motion.span>
                </motion.h1>

                <motion.p
                  className="mt-6 max-w-2xl text-base leading-relaxed text-white/75 md:text-lg"
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.9, delay: 0.55 }}
                >
                  At Kinews Studio Limited, we capture genuine moments with
                  care, creativity, and attention to detail.
                </motion.p>
              </div>

              <motion.div
                className="mt-10 flex flex-wrap gap-4"
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.75, ease: "easeOut" }}
              >
                <Link
                  to="/book"
                  className="rounded-full bg-pink-500 px-7 py-3 text-sm font-medium text-white transition hover:bg-pink-600"
                >
                  Book a Studio Session
                </Link>

                <Link
                  to="/packages"
                  className="rounded-full border border-pink-500/40 px-7 py-3 text-sm text-white transition hover:bg-pink-500/10"
                >
                  View Event Packages
                </Link>
              </motion.div>
            </div>
          </div>
        </section>

        {/* SERVICES */}
        <section className="bg-white">
          <div className="pt-16 text-center">
            <p className="text-sm uppercase tracking-[0.3em] text-pink-500/80">
              Our Services
            </p>
            <h2 className="leading-tight mt-3 text-3xl font-semibold tracking-tight text-black md:text-4xl">
              Bringing your special memories to life.
            </h2>
          </div>

          <div className="mx-auto max-w-6xl px-6 py-14 md:py-14">
            <div className="grid gap-12 md:grid-cols-2">
              {services.map((s) => (
                <div key={s.title} className="text-center">
                  <div className="mx-auto max-w-md">
                    <div className="border border-black/10 bg-white p-3 shadow-[0_20px_60px_rgba(0,0,0,0.15)] transition duration-500 hover:-translate-y-2 hover:shadow-[0_30px_80px_rgba(236,72,153,0.2)] md:p-4">
                      <div className="bg-neutral-100 p-3">
                        <div className="overflow-hidden">
                          <img
                            src={s.img}
                            alt={s.title}
                            className="h-72 w-full object-cover transition duration-700 hover:scale-105"
                            loading="lazy"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <h3 className="mt-6 text-lg font-semibold tracking-wide text-black">
                    {s.title}
                  </h3>

                  <div className="mx-auto mt-2 h-[2px] w-10 bg-pink-500/60" />

                  <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-black/70">
                    {s.desc}
                  </p>

                  <div className="mt-5">
                    <Link
                      to={s.to}
                      className="inline-flex items-center justify-center text-sm font-semibold text-black/70 transition hover:text-pink-500"
                    >
                      {s.cta}
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <HomeGallerySection />

        <HomeShopPreviewSection
          products={shopProducts}
          loading={loadingShopProducts}
          error={shopProductsError}
        />

        {/* PACKAGES */}
        <section className="border-t border-white/10 bg-black">
          <div className="mx-auto max-w-7xl px-6 py-16 md:py-20">
            <div className="text-center">
              <div className="text-sm uppercase tracking-[0.35em] text-pink-400/80">
                Event Packages
              </div>

              <h2 className="mt-3 text-3xl font-semibold tracking-tight md:text-4xl">
                Choose the level of coverage that fits your event.
              </h2>
            </div>

            {loadingPackages ? (
              <div className="mt-12 rounded-2xl border border-white/10 bg-white/[0.04] p-8 text-center text-white/70">
                Loading wedding packages...
              </div>
            ) : packagesError ? (
              <div className="mt-12 rounded-2xl border border-red-500/20 bg-red-500/10 p-8 text-center text-red-200">
                {packagesError}
              </div>
            ) : displayedPackages.length === 0 ? (
              <div className="mt-12 rounded-2xl border border-white/10 bg-white/[0.04] p-8 text-center text-white/70">
                No packages available right now.
              </div>
            ) : (
              <div className="mt-12 grid gap-6 md:grid-cols-3">
                {displayedPackages.map((pkg, index) => {
                  const isPopular = index === 1;

                  return (
                    <div
                      key={pkg.id}
                      className={[
                        "group rounded-2xl p-8 text-center transition duration-300 hover:-translate-y-1",
                        isPopular
                          ? "relative border border-pink-500/40 bg-gradient-to-b from-pink-500/10 to-white/[0.04] shadow-[0_20px_60px_rgba(236,72,153,0.25)]"
                          : "border border-white/10 bg-white/[0.04] hover:border-pink-500/30 hover:bg-white/[0.07]",
                      ].join(" ")}
                    >
                      {isPopular && (
                        <span className="absolute right-4 top-4 rounded-full bg-pink-500 px-3 py-1 text-xs font-semibold text-white">
                          Popular
                        </span>
                      )}

                      <div className="text-sm uppercase tracking-[0.25em] text-white/50">
                        {pkg.name}
                      </div>

                      <div className="mt-4 text-4xl font-semibold text-pink-400">
                        {money(pkg.price)}
                      </div>

                      <p className="mt-4 text-sm text-white/60">
                        {pkg.coverage}
                      </p>

                      <ul className="mt-5 space-y-2 text-sm text-white/70">
                        {pkg.features.map((feature, idx) => (
                          <li key={`${pkg.id}-${idx}`}>{feature}</li>
                        ))}
                      </ul>

                      <Link
                        to="/packages"
                        className={[
                          "mt-8 inline-flex items-center justify-center text-sm font-medium transition",
                          isPopular
                            ? "text-pink-400 hover:text-pink-300"
                            : "text-white/80 hover:text-pink-400",
                        ].join(" ")}
                      >
                        View full package details →
                      </Link>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="mt-14 text-center">
              <Link
                to="/packages"
                className="inline-flex rounded-full border border-pink-500/40 px-6 py-3 text-sm text-pink-400 transition hover:bg-pink-500/10"
              >
                Explore All Packages
              </Link>
            </div>
          </div>
        </section>

        <ReviewsSection />

        <BookSessionCTA />
      </div>
    </>
  );
}
